import React, { useState, useEffect, useMemo } from "react";
import { fmtDate, RECORD_PASS_VID, serverFetch, useGetIsDesktop } from "@extensions";
import { useUser } from "@redux";
import { message, Spin, Card, Table } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useFamilyContext, FamilyContextSelect, ForWhomCell } from "@components/family/family-context";
import "./sales.css";

const RECORD_LOOKAHEAD_DAYS = 180;

const SalesActive = ({ refreshKey = 0, onLoadingChange }) => {
  const user = useUser();
  const isDesktop = useGetIsDesktop();
  const [sales, setSales] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  // Фильтр по группе: состав, выбранный вариант и отбор позиций
  const {
    familyMembers,
    selfClient,
    hasFamily,
    activeContext,
    setActiveContext,
    showForColumn,
    filterByContext,
  } = useFamilyContext(user);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (user) {
      fetchSales();
    }
  }, [user, refreshKey]);

  const fetchSales = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const queryParams = {
        showPaid: true, // Оплаченные
      };

      const data = await serverFetch("OnlineClient/Sales", { method: "GET", queryParams: queryParams }, user);
      let recordsData = [];
      try {
        recordsData = await fetchClientRecords();
      } catch (recordError) {
        console.error("Ошибка получения записей:", recordError);
        message.error(recordError?.UserMessage || "Не удалось загрузить список записей");
      }
      // Фильтруем только активные (оплаченные, но не использованные)
      // Предполагаем, что использованные имеют флаг used или visitFinish
      const activeSales = (data || []).filter(sale => !sale.used && !sale.visitFinish);
      setSales(activeSales);
      setRecords(recordsData);
    } catch (error) {
      console.error("Ошибка получения продаж:", error);
      message.error(error?.UserMessage || "Не удалось загрузить список покупок");
      setSales([]);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return fmtDate(dateStr) || "";
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return dayjs(timeStr, "HH:mm:ss").format("HH:mm");
  };

  const formatPrice = (price) => {
    if (!price) return "0";
    return price.toLocaleString("ru-RU");
  };

  const getClientId = () => user?.clientId;

  const getRecordClientCnt = (record, clientId) => {
    if (!clientId) return null;
    return record?.cnts.find((cnt) => cnt.objectVid === 1 && Number(cnt.objectId) === Number(clientId)) ?? null;
  };

  const getRecordProductName = (record) => {
    const productCnt = record?.cnts.find?.((cnt) => cnt.objectVid === 2);
    return record?.productName || record?.catalogName || productCnt?.objectName || record?.comment || "";
  };

  const isRecordActual = (record) => {
    if (!record?.docDate) return false;
    const recordDate = dayjs(record.docDate).startOf("day");
    const finish = record.finish || record.start;
    if (!finish) return !recordDate.isBefore(dayjs().startOf("day"));
    const finishDateTime = dayjs(`${recordDate.format("YYYY-MM-DD")}T${finish}`);
    return finishDateTime.isValid()
      ? finishDateTime.isAfter(dayjs())
      : !recordDate.isBefore(dayjs().startOf("day"));
  };

  const fetchClientRecords = async () => {
    let clientId = getClientId();
    if (!clientId) {
      const client = await serverFetch("OnlineClient/info", { method: "GET" }, user);
      clientId = client?.id;
    }
    if (!clientId) return [];

    const today = dayjs().startOf("day");
    const data = await serverFetch("Online/Schedule", {
      method: "GET",
      queryParams: {
        from: today.format("YYYY-MM-DD"),
        to: today.add(RECORD_LOOKAHEAD_DAYS, "day").format("YYYY-MM-DD"),
      },
    }, user);

    return (data || [])
      .map((record) => ({
        record,
        clientCnt: getRecordClientCnt(record, clientId),
      }))
      .filter((item) => {
        const passVid = item.record?.passVid;
        return item.clientCnt
          && !item.clientCnt.used
          && !item.clientCnt.visitFinish
          && isRecordActual(item.record)
          && (passVid === RECORD_PASS_VID.ByAbon || passVid === RECORD_PASS_VID.ByPacket);
      })
      .map(({ record }) => record)
      .sort((a, b) => {
        const aDate = `${a.docDate || ""} ${a.start || ""}`;
        const bDate = `${b.docDate || ""} ${b.start || ""}`;
        return aDate.localeCompare(bDate);
      });
  };

  const formatRecordPassVid = (passVid) => {
    if (passVid === RECORD_PASS_VID.ByAbon) return "За абонемент";
    if (passVid === RECORD_PASS_VID.ByPacket) return "За услуги пакета";
    return "";
  };

  const getTicketUrls = (sale) => {
    if (!Array.isArray(sale?.ticketUrls)) return [];
    return sale.ticketUrls.filter((url) => typeof url === "string" && url.trim());
  };

  const openTicketUrl = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderTicketIcons = (sale) => {
    const ticketUrls = getTicketUrls(sale);
    if (!ticketUrls.length) return null;

    return (
      <span className="sales-ticket-icons">
        {ticketUrls.map((url, idx) => (
          <PaperClipOutlined
            key={`${sale.id}-ticket-${idx}`}
            className="sales-ticket-icon"
            onClick={() => openTicketUrl(url)}
            title={`Открыть чек ${idx + 1}`}
          />
        ))}
      </span>
    );
  };

  const formatRecord = (sale) => {
    if (!sale.recordDate) return "";
    const date = formatDate(sale.recordDate);
    const start = sale.recordStart ? formatTime(sale.recordStart) : "";
    const finish = sale.recordFinish ? formatTime(sale.recordFinish) : "";
    if (start && finish) {
      return `${date} ${start}-${finish}`;
    }
    return date;
  };

  const formatScheduleRecord = (record) => {
    if (!record?.docDate) return "";
    const date = formatDate(record.docDate);
    const start = record.start ? formatTime(record.start) : "";
    const finish = record.finish ? formatTime(record.finish) : "";
    if (start && finish) {
      return `${date} ${start}-${finish}`;
    }
    return date;
  };

  const filteredSales = filterByContext(sales);

  // Записи показываем только когда выбраны все сразу или свои:
  // запись конкретного участника уже видна в его покупках
  const filteredRecords = !hasFamily || activeContext === 'all' || activeContext === 'self' ? records : [];

  const columns = useMemo(() => [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Дата",
      dataIndex: "docDate",
      key: "docDate",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Наименование",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Кол.",
      dataIndex: "qty",
      key: "qty",
      width: 80,
      align: "right",
    },
    ...(showForColumn
      ? [
          {
            title: "Для кого",
            key: "for",
            width: 140,
            render: (_, record) => (
              <ForWhomCell clientId={record.clientId} familyMembers={familyMembers} user={selfClient} />
            ),
          },
        ]
      : []),
    {
      title: "Сумма",
      dataIndex: "syma",
      key: "syma",
      width: 120,
      align: "right",
      render: (price, record) => (
        <span className="sales-price-with-tickets">
          {renderTicketIcons(record)}
          <span>{formatPrice(price)} руб.</span>
        </span>
      ),
    },
    {
      title: "Сотрудник",
      dataIndex: "recordUserName",
      key: "recordUserName",
      width: 150,
    },
    {
      title: "Запись",
      key: "record",
      width: 200,
      render: (_, record) => formatRecord(record),
    },
  ], [showForColumn, familyMembers]);

  const recordColumns = [
    {
      title: "Запись",
      key: "record",
      width: 180,
      render: (_, record) => formatScheduleRecord(record),
    },
    {
      title: "Наименование",
      key: "productName",
      render: (_, record) => getRecordProductName(record),
    },
    {
      title: "Способ",
      key: "passVid",
      width: 170,
      render: (_, record) => formatRecordPassVid(record.passVid),
    },
    {
      title: "Сотрудник",
      dataIndex: "userName",
      key: "userName",
      width: 150,
    },
    {
      title: "Зал",
      dataIndex: "roomName",
      key: "roomName",
      width: 140,
    },
  ];

  const renderRecords = () => {
    if (filteredRecords.length === 0) return null;

    return (
      <div className="sales-records-block">
        <div className="sales-records-title">Запись</div>
        {isDesktop ? (
          <Table
            columns={recordColumns}
            dataSource={filteredRecords}
            rowKey="id"
            pagination={false}
            size="small"
            className="sales-table sales-records-table"
          />
        ) : (
          <div className="sales-records-list">
            {filteredRecords.map((record) => (
              <Card key={record.id} className="sales-card sales-record-card" size="small">
                <div className="sales-card-content">
                  <div className="sales-card-header">
                    <div className="sales-card-date">{formatScheduleRecord(record)}</div>
                    <div className="sales-card-record-pass">{formatRecordPassVid(record.passVid)}</div>
                  </div>
                  <div className="sales-card-body">
                    <div className="sales-card-info">
                      {getRecordProductName(record) && (
                        <div className="sales-card-product">{getRecordProductName(record)}</div>
                      )}
                      {record.userName && (
                        <div className="sales-card-employee">Сотрудник: {record.userName}</div>
                      )}
                      {record.roomName && (
                        <div className="sales-card-record">Зал: {record.roomName}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sales-content">
      {hasFamily && (
        <FamilyContextSelect
          familyMembers={familyMembers}
          items={sales}
          activeContext={activeContext}
          onChange={setActiveContext}
          className="sales-context"
          labelClassName="sales-context-label"
          selectClassName="sales-context-select"
        />
      )}
      {loading ? (
        <div className="sales-loading"><Spin size="large" /></div>
      ) : isDesktop ? (
        filteredSales.length > 0 || filteredRecords.length > 0 ? (
          <>
            {filteredSales.length > 0 && (
              <Table columns={columns} dataSource={filteredSales} rowKey="id" pagination={false} className="sales-table"/>
            )}
            {renderRecords()}
          </>
        ) : (
          <div className="sales-empty">Нет активных покупок</div>
        )
      ) : (
        filteredSales.length > 0 || filteredRecords.length > 0 ? (
          <>
            {filteredSales.length > 0 && (
              <div className="sales-list">
                {filteredSales.map((sale) => (
                  <Card key={sale.id} className="sales-card" size="small">
                    <div className="sales-card-content">
                      <div className="sales-card-header">
                        <div className="sales-card-date">{formatDate(sale.docDate)}</div>
                        <div className="sales-card-price">
                          {renderTicketIcons(sale)}
                          <span>{formatPrice(sale.syma)} руб.</span>
                        </div>
                      </div>
                      {showForColumn && (
                        <ForWhomCell
                          clientId={sale.clientId}
                          familyMembers={familyMembers}
                          user={selfClient}
                          unknownInitial="Себе"
                          className="sales-card-for"
                        />
                      )}
                      {sale.deleted && <span className="sales-card-deleted">Удалено</span>}
                      {sale.status === 1 && <span className="sales-card-paid">Оплачено</span>}
                      <div className="sales-card-body">
                        <div className="sales-card-info">
                          {sale.productName && <div className="sales-card-product">{sale.productName}</div>}
                          {sale.qty && (
                            <div className="sales-card-qty">Количество: {sale.qty}</div>
                          )}
                          {sale.recordUserName && (
                            <div className="sales-card-employee">Сотрудник: {sale.recordUserName}</div>
                          )}
                          {sale.recordDate && (
                            <div className="sales-card-record">Запись: {formatRecord(sale)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {renderRecords()}
          </>
        ) : (
          <div className="sales-empty">Нет активных покупок</div>
        )
      )}
    </div>
  );
};

export default SalesActive;
